import { Fragment } from 'react'
import { longDate, shortDate } from '../lib/format'
import type { AdminRegistration } from '../lib/types'

type Props = {
  registrations: AdminRegistration[]
  allSpotIds: string[]
  takenSpotIds: Set<string>
  selectedIds: Set<string>
  expandedId: string | null
  busyId: string | null
  // Archiefmodus: /beheer toont een afgesloten editie. Elke schrijfroute
  // antwoordt daar sowieso 409 `wrong-edition`, dus de knoppen zouden enkel
  // een foutmelding kunnen opleveren — beter ze helemaal niet aanbieden.
  // Ook selecteren valt weg: "stuur bericht" naar vorig jaars standhouders
  // is een heel andere actie dan waar die knop voor bedoeld is, en zou hier
  // per ongeluk gebeuren.
  readOnly?: boolean
  renderEditRow?: (r: AdminRegistration) => React.ReactNode
  onToggleSelect: (id: string) => void
  onToggleAllSelect: () => void
  onAssignSpot: (pageId: string, spotId: string) => void
  onTogglePaid: (pageId: string, paid: boolean) => void
  onToggleExpand: (pageId: string) => void
}

export default function RegistrationsTable({
  registrations,
  allSpotIds,
  takenSpotIds,
  selectedIds,
  expandedId,
  busyId,
  readOnly = false,
  renderEditRow,
  onToggleSelect,
  onToggleAllSelect,
  onAssignSpot,
  onTogglePaid,
  onToggleExpand,
}: Props) {
  const allSelected = registrations.length > 0 && registrations.every((r) => selectedIds.has(r.id))
  // Met het jaartal erbij in de archiefmodus: een tabel die "12/09" toont
  // terwijl je naar 2026 kijkt en de rest van het scherm over 2027 gaat, is
  // precies hoe je edities door elkaar haalt.
  const datum = readOnly ? longDate : shortDate

  return (
    <div className="card overflow-hidden shadow-block">
      <table className="w-full text-sm">
        <thead className="bg-lime text-left font-display tracking-display uppercase">
          <tr>
            <th className="px-3 py-2 w-8">
              {!readOnly && (
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAllSelect}
                  aria-label="Alles selecteren"
                />
              )}
            </th>
            <th className="px-3 py-2">Naam</th>
            <th className="px-3 py-2">Datum</th>
            <th className="px-3 py-2">E-mail</th>
            <th className="px-3 py-2">Gewenst</th>
            <th className="px-3 py-2">Plaatsen</th>
            <th className="px-3 py-2">Plek</th>
            <th className="px-3 py-2">{readOnly ? 'Adres' : 'Toewijzen'}</th>
            <th className="px-3 py-2">Betaald</th>
            <th className="px-3 py-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {registrations.map((r) => {
            const expanded = expandedId === r.id
            return (
              <Fragment key={r.id}>
                <tr className={`border-t border-line ${expanded ? 'bg-lime/30' : ''}`}>
                  <td className="px-3 py-2 align-top">
                    {!readOnly && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => onToggleSelect(r.id)}
                        aria-label={`Selecteer ${r.naam}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {r.buurtbewoner && <span title="Buurtbewoner">🏠 </span>}
                    {r.wachtlijst && <span title="Wachtlijst">⏳ </span>}
                    {r.naam}
                  </td>
                  <td className="px-3 py-2 align-top text-muted whitespace-nowrap">{datum(r.inschrijvingsdatum)}</td>
                  <td className="px-3 py-2 align-top text-muted">{r.email || <em>(geen)</em>}</td>
                  <td className="px-3 py-2 align-top">
                    {r.gewenstAdres}
                    {r.notes && <div className="text-muted text-xs mt-0.5">"{r.notes}"</div>}
                  </td>
                  <td className="px-3 py-2 align-top">{r.aantalPlaatsen}</td>
                  <td className="px-3 py-2 align-top">
                    {r.spots.length > 0 ? <span className="pill">{r.spots.join(', ')}</span> : <span className="text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {readOnly ? (
                      // Het bevroren adres van toen — de enige plek-informatie
                      // die een afgesloten editie nog betrouwbaar heeft: `spots`
                      // bevat ruwe polygoon-id's die een latere kaart hergebruikt.
                      r.plekAdresLabels.length > 0
                        ? <span className="text-muted">{r.plekAdresLabels.join(' · ')}</span>
                        : r.spots.length > 0
                          // Wel een plek, geen bevroren adres: dat is precies wat
                          // de rode "nog niet bevroren"-regel bij Seizoen bedoelt.
                          // Hier "—" tonen zou het verbergen.
                          ? <span className="text-red">niet bevroren</span>
                          : <span className="text-muted">—</span>
                    ) : (
                    <select
                      className="input"
                      disabled={busyId === r.id}
                      value={r.spots[0] ?? ''}
                      onChange={(e) => onAssignSpot(r.id, e.target.value)}
                    >
                      <option value="">— geen —</option>
                      {allSpotIds.map((id) => {
                        const isMine = r.spots.includes(id)
                        const occupied = takenSpotIds.has(id) && !isMine
                        return (
                          <option key={id} value={id} disabled={occupied}>
                            {id}{occupied ? ' (bezet)' : ''}
                          </option>
                        )
                      })}
                    </select>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-center">
                    <input
                      type="checkbox"
                      checked={r.betaald}
                      disabled={readOnly || busyId === r.id}
                      onChange={(e) => onTogglePaid(r.id, e.target.checked)}
                      aria-label={`Betaald ${r.naam}`}
                    />
                  </td>
                  <td className="px-3 py-2 align-top text-center">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => onToggleExpand(r.id)}
                        className="text-lg leading-none hover:text-ink"
                        aria-label={expanded ? 'Sluit bewerken' : 'Bewerk'}
                        title={expanded ? 'Sluit bewerken' : 'Bewerk'}
                      >
                        {expanded ? '×' : '✎'}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded && renderEditRow && (
                  <tr className="border-t border-line">
                    <td colSpan={10} className="px-3 py-2 bg-lime/20">
                      {renderEditRow(r)}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
